import { useEffect, useMemo, useState } from "react";
import { api, ABCProduct, ABCResponse, ABCClass } from "../api";
import { formatVND, formatNumber } from "../utils";
import { searchItems } from "../search";
import { GlassPanel, KpiCard, SkeletonTable, SkeletonKpi, ErrorState, DataTable, Badge, EmptyState } from "../components";
import type { Column } from "../components";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const CLASS_COLORS: Record<ABCClass, string> = {
  A: "#ff6b35",
  B: "#ffc107",
  C: "#6b7280",
};

const CLASS_LABELS: Record<ABCClass, string> = {
  A: "Hạng A (top 70%)",
  B: "Hạng B (70-90%)",
  C: "Hạng C (90-100%)",
};

type FilterClass = "all" | ABCClass;

export default function ABCAnalysisPage() {
  const [data, setData] = useState<ABCResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState<FilterClass>("all");
  const [search, setSearch] = useState("");
  const [dataDays, setDataDays] = useState<number | null>(null);

  const load = (d: number) => {
    setLoading(true);
    setError(null);
    api.inventoryABC(d)
      .then((res) => setData(res))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(days); }, [days]);

  // Check data maturity
  useEffect(() => {
    fetch(`${api.baseUrl}/api/data-maturity`, { headers: api.authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((d: any) => setDataDays(d.days_with_receipts ?? 999))
      .catch(() => setDataDays(999));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.products;
    if (filter !== "all") rows = rows.filter((r) => r.class === filter);
    if (search.trim()) {
      rows = searchItems(rows, search, (r) => ({
        id: r.product_id,
        sku: r.sku,
        name: r.name,
      })).map(({ item }) => item);
    }
    return rows;
  }, [data, filter, search]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return [
      { name: "A", value: data.summary.revenue_a, color: CLASS_COLORS.A },
      { name: "B", value: data.summary.revenue_b, color: CLASS_COLORS.B },
      { name: "C", value: data.summary.revenue_c, color: CLASS_COLORS.C },
    ].filter((d) => d.value > 0);
  }, [data]);

  const classBadge = (cls: ABCClass) => {
    switch (cls) {
      case "A": return <Badge variant="bad">A</Badge>;
      case "B": return <Badge variant="warn">B</Badge>;
      case "C": return <Badge variant="info">C</Badge>;
    }
  };

  const columns: Column<ABCProduct>[] = useMemo(() => [
    {
      key: "class",
      header: "Hạng",
      render: (r) => classBadge(r.class),
    },
    {
      key: "sku",
      header: "SKU",
      sortable: true,
      render: (r) => <span className="meta">{r.sku || "—"}</span>,
    },
    {
      key: "name",
      header: "Tên",
      sortable: true,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          {r.category && <div className="meta" style={{ marginTop: 2 }}>{r.category}</div>}
        </div>
      ),
    },
    {
      key: "qty_sold",
      header: "SL bán",
      sortable: true,
      align: "right",
      render: (r) => formatNumber(r.qty_sold),
    },
    {
      key: "revenue",
      header: "Doanh thu",
      sortable: true,
      align: "right",
      render: (r) => <span style={{ fontWeight: 600 }}>{formatVND(r.revenue)}</span>,
    },
    {
      key: "revenue_pct",
      header: "% tổng",
      sortable: true,
      align: "right",
      render: (r) => `${r.revenue_pct.toFixed(2)}%`,
    },
    {
      key: "cumulative_pct",
      header: "Cộng dồn %",
      sortable: true,
      align: "right",
      render: (r) => (
        <span style={{ color: r.cumulative_pct <= 70 ? CLASS_COLORS.A : r.cumulative_pct <= 90 ? CLASS_COLORS.B : CLASS_COLORS.C }}>
          {r.cumulative_pct.toFixed(2)}%
        </span>
      ),
    },
  ], []);

  if (error && !data) {
    return <ErrorState error={error} onRetry={() => load(days)} />;
  }

  if (dataDays !== null && dataDays < 30) {
    return (
      <EmptyState
        state="not-enough-data"
        icon="analytics"
        title="Cần 30 ngày bán hàng để phân tích ABC"
        description="Phân tích ABC cần ít nhất 30 ngày dữ liệu doanh thu để phân loại sản phẩm chính xác."
        actualDays={dataDays}
        requiredDays={30}
        cta={{ label: "Xem đơn hàng", href: "/orders" }}
      />
    );
  }

  return (
    <>
      {/* Period selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <label className="meta">Khoảng thời gian:</label>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="input"
          style={{ minWidth: 120 }}
        >
          <option value={30}>30 ngày</option>
          <option value={60}>60 ngày</option>
          <option value={90}>90 ngày</option>
        </select>
      </div>

      {/* KPI Cards */}
      {loading && !data ? (
        <div className="kpi-grid">
          <SkeletonKpi /><SkeletonKpi /><SkeletonKpi />
        </div>
      ) : data && (
        <div className="kpi-grid">
          <KpiCard
            label="Hạng A"
            value={`${data.summary.count_a} SP`}
            note={`${formatVND(data.summary.revenue_a)} (${data.summary.total_revenue > 0 ? Math.round(data.summary.revenue_a / data.summary.total_revenue * 100) : 0}%)`}
            dotColor="bad"
          />
          <KpiCard
            label="Hạng B"
            value={`${data.summary.count_b} SP`}
            note={`${formatVND(data.summary.revenue_b)} (${data.summary.total_revenue > 0 ? Math.round(data.summary.revenue_b / data.summary.total_revenue * 100) : 0}%)`}
            dotColor="warn"
          />
          <KpiCard
            label="Hạng C"
            value={`${data.summary.count_c} SP`}
            note={`${formatVND(data.summary.revenue_c)} (${data.summary.total_revenue > 0 ? Math.round(data.summary.revenue_c / data.summary.total_revenue * 100) : 0}%)`}
            dotColor=""
          />
        </div>
      )}

      {/* Pie Chart */}
      {data && pieData.length > 0 && (
        <GlassPanel title="Tỷ trọng doanh thu A/B/C" style={{ marginBottom: 24 }}>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatVND(v)} />
                <Legend
                  formatter={(value) => CLASS_LABELS[value as ABCClass] || value}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </GlassPanel>
      )}

      {/* Table */}
      <GlassPanel
        title="Chi tiết phân loại ABC"
        badge={`${filtered.length} sản phẩm`}
        badgeVariant="info"
      >
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterClass)}
            className="input"
            style={{ minWidth: 140 }}
          >
            <option value="all">Tất cả</option>
            <option value="A">Hạng A</option>
            <option value="B">Hạng B</option>
            <option value="C">Hạng C</option>
          </select>
          <input
            type="text"
            className="input"
            placeholder="Tìm theo tên / SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 180 }}
          />
        </div>

        {loading && !data ? (
          <SkeletonTable rows={8} cols={7} />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>
            {data && data.products.length === 0
              ? "Chưa có dữ liệu bán hàng trong khoảng thời gian này."
              : "Không tìm thấy sản phẩm phù hợp."}
          </div>
        ) : (
          <div className="table-wrap">
            <DataTable columns={columns} rows={filtered} rowKey={(r) => r.product_id} pageSize={50} />
          </div>
        )}
      </GlassPanel>
    </>
  );
}
